// Cloudinary url api:
// http://cloudinary.com/documentation/image_transformations#reference

import type { FC } from 'react';

import { getFallbackImgUrl, getTransformedImageUrl } from '../../utils/cloudinaryImageUtils';
import Image from './Image';

export const removeStartingSlash = (str: string | null | undefined) => {
    if (!str) {
        return str;
    }

    if (str[0] === '/') {
        return str.slice(1);
    }

    return str;
};

export const CLOUDINARY_ASPECT_RATIO_TRANSFORMS = {
    ratio16By9: {
        transform: `e_sharpen,f_auto,fl_progressive,q_auto,c_lpad,g_center,ar_${16 / 9},c_crop`,
        cssPadding: (9 / 16) * 100,
    },
};

interface CloudinaryImageProps {
    // Used for Cloudinary SEO-friendly URL path (and as HTML alt when `alt` is omitted)
    name: string;
    // HTML alt override; when set (including ""), used instead of `name` for the img alt
    alt?: string;
    // title tag of image
    title?: string;
    url: string | undefined;
    // if url is missing, this will be fallback
    fallbackUrl?: string;
    retinaTransform?: string;
    transform: string;
    cssClass?: string;
    lazy?: boolean;
    fileExtenson?: string;
    width?: string | number;
    height?: string | number;
    style?: React.CSSProperties;
    tabIndex?: number;
}

const getHtmlAlt = (props: CloudinaryImageProps) =>
    props.alt !== undefined ? props.alt : props.name;

const FallbackImg = (props: CloudinaryImageProps) => {
    return (
        <img
            style={props.style}
            className={props.cssClass}
            alt={getHtmlAlt(props)}
            title={props.title}
            src={getFallbackImgUrl(props.fallbackUrl)}
            width={props.width}
            height={props.height}
        />
    );
};

interface TransformedImageProps extends Omit<CloudinaryImageProps, 'url'> {
    url: string;
}

const TransformedImage = (props: TransformedImageProps) => {
    const transformedUrl = getTransformedImageUrl(
        props.url,
        props.transform,
        props.fileExtenson,
        undefined,
        props.name,
    );
    const srcSet = props.retinaTransform
        ? getTransformedImageUrl(
              props.url,
              props.transform,
              props.fileExtenson,
              props.retinaTransform,
              props.name,
          )
        : undefined;

    return (
        <Image
            tabIndex={props.tabIndex}
            width={props.width}
            height={props.height}
            alt={getHtmlAlt(props)}
            title={props.title}
            src={transformedUrl}
            srcSet={srcSet}
            fallbackSrc={props.fallbackUrl}
            cssClass={props.cssClass}
            lazy={props.lazy}
            style={props.style}
        />
    );
};

const CloudinaryImage: FC<React.PropsWithChildren<CloudinaryImageProps>> = (
    props: CloudinaryImageProps,
) => {
    if (!props.url) {
        return FallbackImg(props);
    }
    return TransformedImage({ ...props, url: props.url });
};

export default CloudinaryImage;
